import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiGet, apiPut, apiPost } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { inputCls } from "@/components/common/states";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Qualified", "Site Visit", "Converted", "Lost"];
const COLORS = {
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  Qualified: "bg-purple-100 text-purple-700", "Site Visit": "bg-cyan-100 text-cyan-700",
  Converted: "bg-green-100 text-green-700", Lost: "bg-red-100 text-red-700",
};

export default function AdminLeads() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  useSEO({ title: "Leads · Admin" });

  const status = params.get("status") || "";

  const load = useCallback(() => {
    const q = {};
    if (status) q.status = status;
    if (search) q.search = search;
    apiGet("/admin/leads", q).then(setData).catch(() => {});
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  const openLead = useCallback((id) => {
    apiGet(`/admin/leads/${id}`).then((d) => setDetail(d)).catch(() => {});
  }, []);

  useEffect(() => {
    const open = params.get("open");
    if (open) openLead(open);
  }, [params, openLead]);

  const changeStatus = async (id, newStatus) => {
    await apiPut(`/admin/leads/${id}`, { status: newStatus });
    toast.success("Status updated");
    load();
    if (detail?.lead?.id === id) setDetail((d) => ({ ...d, lead: { ...d.lead, status: newStatus } }));
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const res = await apiPost(`/admin/leads/${detail.lead.id}/notes`, { text: note });
    if (res.success) {
      setDetail((d) => ({ ...d, lead: { ...d.lead, notes: [...(d.lead.notes || []), res.data.note] } }));
      setNote("");
      toast.success("Note added");
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl text-foreground mb-1">Leads</h1>
      <p className="text-muted-foreground mb-6">{data?.total ?? 0} total leads</p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button onClick={() => setParams({})} className={`px-3 py-1.5 rounded-sm text-sm font-medium ${!status ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`filter-status-${s}`} onClick={() => setParams({ status: s })}
            className={`px-3 py-1.5 rounded-sm text-sm font-medium ${status === s ? "bg-primary text-primary-foreground" : "bg-white border border-border"}`}>{s}</button>
        ))}
        <input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email"
          className={`${inputCls} h-9 w-64 ml-auto`} />
      </div>

      <div className="rounded-lg border border-border bg-white overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left p-4">Name</th><th className="text-left p-4">Contact</th>
              <th className="text-left p-4">Source</th><th className="text-left p-4">Interest</th>
              <th className="text-left p-4">Status</th><th className="text-left p-4">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-sm">
            {data?.items?.length ? data.items.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => openLead(l.id)} className="hover:bg-muted/40 cursor-pointer">
                <td className="p-4 font-medium text-foreground">{l.name}</td>
                <td className="p-4 text-muted-foreground">{l.phone}{l.email ? <><br /><span className="text-xs">{l.email}</span></> : null}</td>
                <td className="p-4 text-muted-foreground">{l.source}</td>
                <td className="p-4 text-muted-foreground">{l.property_name || l.location || l.configuration || "—"}</td>
                <td className="p-4"><span className={`text-[11px] font-semibold px-2 py-1 rounded-sm ${COLORS[l.status]}`}>{l.status}</span></td>
                <td className="p-4 text-muted-foreground text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
              </tr>
            )) : <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No leads found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); if (params.get("open")) setParams(status ? { status } : {}); } }}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{detail.lead.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} data-testid={`set-status-${s}`} onClick={() => changeStatus(detail.lead.id, s)}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-sm border transition-colors ${detail.lead.status === s ? `${COLORS[s]} border-transparent` : "border-border text-muted-foreground hover:border-primary"}`}>{s}</button>
                  ))}
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <Field label="Phone" value={detail.lead.phone} />
                  <Field label="Email" value={detail.lead.email} />
                  <Field label="Source" value={detail.lead.source} />
                  <Field label="Location" value={detail.lead.location} />
                  <Field label="Property" value={detail.lead.property_name} />
                  <Field label="Type" value={detail.lead.property_type} />
                  <Field label="Budget" value={detail.lead.budget} />
                  <Field label="Configuration" value={detail.lead.configuration} />
                  <Field label="Purpose" value={detail.lead.purpose} />
                  <Field label="Timeline" value={detail.lead.timeline} />
                  <Field label="UTM Source" value={detail.lead.utm_source} />
                  <Field label="Campaign" value={detail.lead.utm_campaign} />
                </dl>
                {detail.lead.message && <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Message</p><p className="text-sm text-foreground mt-1">{detail.lead.message}</p></div>}
                {detail.lead.landing_page && <p className="text-xs text-muted-foreground">Landing: {detail.lead.landing_page}</p>}

                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Notes</p>
                  <div className="space-y-2 mb-3">
                    {detail.lead.notes?.length ? detail.lead.notes.map((n, i) => (
                      <div key={i} className="text-sm bg-muted p-3 rounded-sm"><p className="text-foreground">{n.text}</p><p className="text-[11px] text-muted-foreground mt-1">{n.by} · {new Date(n.at).toLocaleString()}</p></div>
                    )) : <p className="text-sm text-muted-foreground">No notes yet.</p>}
                  </div>
                  <div className="flex gap-2">
                    <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note" className={`${inputCls} h-10`} />
                    <Button data-testid="note-add" onClick={addNote} className="bg-primary text-primary-foreground h-10 shrink-0">Add</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return <div><dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</dt><dd className="text-foreground">{value}</dd></div>;
}
